import { Handshake, User } from "lucide-react";
import type { Contracts } from "@/src/gql/graphql";
import type { DetailHeaderModel, HeaderFact } from "../model";

/**
 * Fields the contract header reads. Guarded against the GetContract query by
 * tests/crm-entities/detail-header-model-coverage.test.ts — a field listed here
 * that the detail query stops selecting fails the build instead of rendering
 * blank. `deals` is the nested relation (contract → deal via contracts.deal_id)
 * so the header can show the deal's TITLE, not just the id.
 */
export const CONTRACT_HEADER_FIELDS = [
  "deal_id",
  "deals",
  "recipient_name",
] as const satisfies readonly (keyof Contracts)[];

export type ContractHeaderData = Pick<Contracts, (typeof CONTRACT_HEADER_FIELDS)[number]>;

/** Nothing on the contract header is edited in place (provider-owned document). */
export const CONTRACT_HEADER_EDITABLE_FIELDS = [] as const satisfies readonly (keyof Contracts)[];

/**
 * Contract detail header: a 🤝 backlink to the deal this contract belongs to
 * (the reverse of the deal's Contracts tab), plus the recipient. The contract's
 * status pill + document-name title come from the shared header path, so this
 * model only adds the deal linkage + recipient context. Mirrors the Slack
 * contract card's deal backlink so Hub and Slack read as one system.
 */
export function buildContractHeaderModel(contract: ContractHeaderData): DetailHeaderModel {
  const facts: HeaderFact[] = [];

  if (contract.deal_id) {
    const dealTitle = contract.deals?.title?.trim();
    facts.push({
      id: "deal",
      icon: Handshake,
      label: "Deal",
      value: dealTitle && dealTitle.length > 0 ? dealTitle : "View deal",
      kind: "internal",
      href: `/deals/${contract.deal_id}`,
    });
  }

  if (contract.recipient_name) {
    facts.push({ id: "recipient", icon: User, value: contract.recipient_name });
  }

  return { subtitleSegments: [], facts, metrics: [] };
}
